import { IssueOnSingleView } from 'components/dataProviders/withIssue/IssueOnSingleView';
import { isRouteProjectSelected } from 'components/dataProviders/withIssue/model';
import { areObjectIdsEqual } from 'helpers/idComparison';
import { DocumentsOnIssueView } from 'presentation/document/documentsOnIssueView';
import { Identificable } from 'shared/types/commonView';
import {
  DeepLinkResolution,
  DeepLinkTarget,
  ENTITY,
  EntityStatuses,
  EntityStatusType,
  IssueDeepLink,
  IssueLoadState,
  RouteProjectState,
} from './types';
import { IssueReferenceName } from 'shared/domain/issue/issueReferences';

export function getRouteProjectState(
  routeProjectId: string | undefined,
  selectedProjectId: string | undefined,
  projects: Identificable[],
  isProjectListLoaded: boolean
): RouteProjectState {
  return {
    isSelected: isRouteProjectSelected(routeProjectId, selectedProjectId),
    isProjectListLoaded,
    isAccessible: projects.some((project) =>
      areObjectIdsEqual(project._id, routeProjectId)
    ),
  };
}

export function clearEntityStatus(
  status: EntityStatusType | undefined,
  entity: ENTITY
): EntityStatusType | undefined {
  return status?.entity === entity ? undefined : status;
}

const ISSUE_REFERENCE_ENTITIES: Record<IssueReferenceName, ENTITY> = {
  [IssueReferenceName.SITE]: ENTITY.SITE,
  [IssueReferenceName.LEVEL]: ENTITY.LEVEL,
  [IssueReferenceName.PROCESS]: ENTITY.PROCESS,
};

/** Entities whose status follows the issue: the issue itself and what it refers to. */
const ISSUE_ENTITIES = new Set<ENTITY>([ENTITY.ISSUE, ...Object.values(ISSUE_REFERENCE_ENTITIES)]);

function notFound(entity: ENTITY): EntityStatusType {
  return { entity, status: EntityStatuses.NOT_FOUND };
}

/**
 * "Not found" for an issue that could not be read, or for the site, level
 * or process it refers to and cannot be shown without.
 */
export function resolveIssueEntityStatus(
  status: EntityStatusType | undefined,
  { hasError, missingReference }: IssueLoadState
): EntityStatusType | undefined {
  if (hasError) {
    return notFound(ENTITY.ISSUE);
  }
  if (missingReference) {
    return notFound(ISSUE_REFERENCE_ENTITIES[missingReference]);
  }
  return status && ISSUE_ENTITIES.has(status.entity) ? undefined : status;
}

/**
 * An accessible project from the URL is handled by the project change
 * dialog, so "not found" is shown only for projects the user cannot open.
 */
export function resolveProjectEntityStatus(
  status: EntityStatusType | undefined,
  { isSelected, isProjectListLoaded, isAccessible }: RouteProjectState
): EntityStatusType | undefined {
  if (isSelected) {
    return clearEntityStatus(status, ENTITY.PROJECT);
  }
  if (!isProjectListLoaded) {
    return status;
  }
  if (isAccessible) {
    return clearEntityStatus(status, ENTITY.PROJECT);
  }
  return { entity: ENTITY.PROJECT, status: EntityStatuses.NOT_FOUND };
}

/**
 * The status after the issue error and the route project are taken into
 * account; a missing project takes precedence over a missing issue.
 */
export function resolveEntityStatus(
  status: EntityStatusType | undefined,
  issueLoadState: IssueLoadState,
  routeProjectState: RouteProjectState
): EntityStatusType | undefined {
  return resolveProjectEntityStatus(resolveIssueEntityStatus(status, issueLoadState), routeProjectState);
}

export function isSameEntityStatus(
  first: EntityStatusType | undefined,
  second: EntityStatusType | undefined
): boolean {
  return first?.entity === second?.entity && first?.status === second?.status;
}

const shown: DeepLinkResolution = { target: DeepLinkTarget.shown };
const toIssue: DeepLinkResolution = { target: DeepLinkTarget.issue };
const missing = (entity: ENTITY, status: EntityStatuses): DeepLinkResolution => ({
  target: DeepLinkTarget.missing,
  status: { entity, status },
});

type DeepLinkedIssue = Pick<
  IssueOnSingleView,
  'events' | 'incomingEmailMessages' | 'linkedDocumentation' | 'workflowDecisions'
>;

/**
 * What a deep link into the loaded issue leads to; undefined without one. A stale link to a
 * document goes back to the issue when it can (`canShowIssue`): its file was removed from the
 * preview, or it was linked to the documentation since.
 */
export function resolveDeepLink(
  { eventId, documentId, linkedDocumentationVersionId }: IssueDeepLink,
  issue: DeepLinkedIssue,
  documents: DocumentsOnIssueView,
  canShowIssue: boolean
): DeepLinkResolution | undefined {
  if (!eventId && !documentId && !linkedDocumentationVersionId) {
    return undefined;
  }

  if (eventId) {
    const event = [...issue.events, ...issue.incomingEmailMessages].find((candidate) => candidate._id === eventId);
    const decision = (issue.workflowDecisions ?? []).find((candidate) => candidate._id === eventId);
    if (!event && !decision) {
      return missing(ENTITY.EVENT, EntityStatuses.NOT_FOUND);
    }
    if (event?.deleted) {
      return missing(ENTITY.EVENT, EntityStatuses.DELETED);
    }
  }

  const linkedDocumentation = issue.linkedDocumentation ?? [];

  if (linkedDocumentationVersionId) {
    if (linkedDocumentation.some((item) => String(item.versionId) === linkedDocumentationVersionId)) {
      return shown;
    }
    return canShowIssue ? toIssue : missing(ENTITY.DOCUMENT, EntityStatuses.NOT_FOUND);
  }

  if (!documentId) {
    return shown;
  }

  const candidates = eventId
    ? documents.eventDocuments[eventId] || documents.incomingEmailMessageDocuments[eventId]
    : documents.issueDocuments;
  const document = candidates?.find((candidate) => candidate._id === documentId);

  if (!document) {
    const linkedFromDocument = linkedDocumentation.find(
      (item) => item.sourceDocumentId === documentId
    );
    const isStale = Boolean(linkedFromDocument?.versionId) || !eventId;
    return isStale && canShowIssue ? toIssue : missing(ENTITY.DOCUMENT, EntityStatuses.NOT_FOUND);
  }
  return document.deleted ? missing(ENTITY.DOCUMENT, EntityStatuses.DELETED) : shown;
}
